import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "../lib/notify";
import { http, toastApiError, getUserLabels } from "../lib/api";
import { recordLink } from "../lib/notify";
import { useNavDraft } from "../lib/navDraft";
import { fmtTL } from "../lib/format";
import { parseTL } from "../components/AmountInput";
import { useAuth } from "../context/AuthContext";
import AssetBulkAssign from "../components/AssetBulkAssign";
import ZimmetReport from "../components/ZimmetReport";
import AssetCount from "../components/AssetCount";
import DepreciationSchedule from "../components/DepreciationSchedule";
import AssetToolbar from "../components/assets/AssetToolbar";
import AssetForm from "../components/assets/AssetForm";
import { uploadPendingDocs } from "../components/assets/AssetDocGroup";
import { DEED_DOCS } from "../components/assets/DeedSection";
import { VEHICLE_DOCS } from "../components/assets/VehicleSection";
import AssetTable from "../components/assets/AssetTable";
import AssetBulkUpload from "../components/assets/AssetBulkUpload";
import DisposeForm from "../components/assets/DisposeForm";
import { WarrantyBanner, MaintenanceDueBanner, InsuranceBanner, QrBar } from "../components/assets/AssetBanners";
import { usePanels } from "../components/assets/usePanels";
import { emptyAsset, emptyDispose, depreciationYears, isDeedNature, isVehicleNature } from "../components/assets/assetShared";

const trName = (k) => (a, b) => (a[k] || "").localeCompare(b[k] || "", "tr");
const depLabel = (d) => `${d.code} - ${d.name} (${d.useful_life} yıl, %${String(d.rate).replace(".", ",")})`;
const DISPOSED_GROUP = "ZZZ_HURDA / SATILAN (amortisman takibi dışı)";

// kayıtlar mahiyet (üst hesap) bazında gruplanır; hurda/satılanlar ayrı grupta (varsayılan gizli)
function groupAssets(items, showDisposed) {
  const m = new Map();
  items.forEach((a) => {
    if (a.disposal && !showDisposed) return;
    const k = a.disposal ? DISPOSED_GROUP : (a.nature || "Üst hesap seçilmemiş");
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(a);
  });
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], "tr")).map(([k, v]) => [k.replace("ZZZ_", ""), v]);
}

// tutar alanı: sayı geldiyse aynen, metin geldiyse TL biçiminden çöz
const toNum = (v) => (typeof v === "number" ? v : parseTL(String(v ?? "")) ?? 0);

// form → API gövdesi (sunucu türetilen alanlar hariç); tapu / araç bilgileri yalnız ilgili mahiyette gönderilir
function assetPayload(form) {
  const { assignment_history, remaining_years, last_depreciation_year, photo_path, photo_ver, deed, vehicle, pending_docs, ...rest } = form;
  const { life } = depreciationYears(form);
  return {
    ...rest,
    cost_value: parseTL(form.cost_value) ?? 0,
    useful_life_years: life > 0 ? life : null,
    depreciation_rate: form.depreciation_rate === "" || form.depreciation_rate == null ? null : Number(form.depreciation_rate),
    purchase_date: form.purchase_date || null,
    assigned_at: form.assigned_at || null,
    warranty_end: form.warranty_end || null,
    warranty_years: form.warranty_years === "" || form.warranty_years == null ? null : parseInt(form.warranty_years, 10),
    deed: isDeedNature(form.nature) && deed ? { ...deed, market_value: toNum(deed.market_value), appraisal_value: toNum(deed.appraisal_value) } : null,
    vehicle: isVehicleNature(form.nature) && vehicle ? { ...vehicle, market_value: toNum(vehicle.market_value) } : null,
  };
}

// tanım verileri: amortisman kodları, projeler, personel, kullanıcı etiketleri
function useAssetLookups() {
  const [depCodes, setDepCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [userLabels, setUserLabels] = useState({});
  useEffect(() => {
    http.get("/depreciation-codes").then((r) => setDepCodes(r.data.filter((d) => d.rate != null))).catch(() => {});
    http.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    http.get("/personnel").then((r) => setPersonnel(r.data)).catch(() => {});
    getUserLabels().then(setUserLabels);
  }, []);
  const depOptions = useMemo(() => depCodes.map((d) => ({ value: d.code, label: depLabel(d) })), [depCodes]);
  const projectOptions = useMemo(() => projects.filter((p) => p.status === "Aktif").sort(trName("name")).map((p) => ({ value: p.id, label: p.name })), [projects]);
  return { depCodes, projects, personnel, userLabels, depOptions, projectOptions };
}

// hurda / satış akışı
function useDispose(load) {
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState(emptyDispose());
  const open = (a) => { setTarget(a); setForm(emptyDispose()); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const save = async () => {
    setBusy(true);
    try {
      await http.post(`/fixed-assets/${target.id}/dispose`, { ...form, amount: parseTL(String(form.amount || "")) ?? 0 });
      toast.success(`${target.name} ${form.type === "Satış" ? "satıldı" : "hurdaya ayrıldı"} — amortisman takibinden düşürüldü`);
      setTarget(null); load();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };
  const undo = async (a) => {
    if (!window.confirm(`${a.name} için hurda/satış kaydı geri alınsın mı?`)) return;
    try { await http.delete(`/fixed-assets/${a.id}/dispose`); load(); } catch (e) { toastApiError(e); }
  };
  return { busy, target, setTarget, form, setForm, open, save, undo };
}

export default function Demirbaslar() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const { depCodes, projects, personnel, userLabels, depOptions, projectOptions } = useAssetLookups();
  const [showDisposed, setShowDisposed] = useState(false);
  const [editing, setEditing] = useState(null); // 'new' | id
  const [form, setForm] = useState(emptyAsset);
  useNavDraft("demirbas", () => (editing ? { editing, form, showDisposed } : null), (d) => { setEditing(d.editing); setForm(d.form); if (typeof d.showDisposed === "boolean") setShowDisposed(d.showDisposed); });

  // ESC: açık demirbaş kartını kapat (filtreler korunur); ?kayit= varken uygulama geneli ESC (geri dön) devreye girer
  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => { if (e.key !== "Escape" || new URLSearchParams(window.location.search).get("kayit")) return; e.preventDefault(); setEditing(null); setForm(emptyAsset); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing]);
  const pickDep = (code) => { const d = depCodes.find((x) => x.code === code); setForm({ ...form, depreciation_code: code || "", useful_life_years: d ? d.useful_life ?? "" : form.useful_life_years, depreciation_rate: d ? d.rate : "" }); };

  const load = () => http.get("/fixed-assets").then((r) => setItems(r.data)).catch((e) => toastApiError(e));
  useEffect(() => { load(); }, []);

  const disposedCount = useMemo(() => items.filter((a) => a.disposal).length, [items]);
  const grouped = useMemo(() => groupAssets(items, showDisposed), [items, showDisposed]);

  const startNew = () => { setForm(emptyAsset); setEditing("new"); };
  const startEdit = useCallback((a) => {
    setForm({ ...emptyAsset, ...a, cost_value: a.cost_value ? fmtTL(a.cost_value) : "", useful_life_years: a.useful_life_years ?? "", depreciation_rate: a.depreciation_rate ?? "", purchase_date: a.purchase_date || "", assigned_at: a.assigned_at || "", warranty_end: a.warranty_end || "", warranty_years: a.warranty_years ?? "" });
    setEditing(a.id);
  }, []);
  const openById = (id) => { const a = items.find((x) => x.id === id); if (a) startEdit(a); };
  const cancel = () => { setEditing(null); setForm(emptyAsset); };
  const assigneeOptions = useMemo(() => personnel
    .filter((p) => p.status !== "Pasif" && (!form.project_id || p.project_id === form.project_id))
    .sort(trName("full_name"))
    .map((p) => ({ value: p.id, label: `${p.tc_no || "—"} - ${p.full_name || ""}`, keywords: `${p.tc_no || ""} ${p.full_name || ""} ${p.registry_no || ""}` })), [personnel, form.project_id]);
  const pickProject = (v) => setForm({ ...form, project_id: v || "", project_name: projects.find((p) => p.id === v)?.name || "", assignee_personnel_id: "", assignee_name: "" });
  const pickAssignee = (v) => setForm({ ...form, assignee_personnel_id: v || "", assignee_name: personnel.find((p) => p.id === v)?.full_name || "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Demirbaş adı zorunlu");
    try {
      const payload = assetPayload(form);
      const saved = editing === "new" ? (await http.post("/fixed-assets", payload)).data : (await http.put(`/fixed-assets/${editing}`, payload)).data;
      const newId = editing === "new" ? saved?.id : editing;
      const pendingCount = Object.values(form.pending_docs || {}).filter(Boolean).length;
      const uploaded = editing === "new" && pendingCount && newId ? await uploadPendingDocs(newId, form.pending_docs, { ...DEED_DOCS, ...VEHICLE_DOCS }) : 0;
      toast.success(editing === "new" ? `Demirbaş eklendi${uploaded ? ` · ${uploaded} görsel yüklendi` : ""}` : "Demirbaş güncellendi", { link: recordLink(newId) });
      cancel();
      load();
    } catch (err) { toastApiError(err); }
  };
  const remove = async (a) => {
    if (!window.confirm(`"${a.name}" demirbaşı silinsin mi? Silinen kayıt Silinenler'e taşınır.`)) return;
    try {
      await http.delete(`/fixed-assets/${a.id}`);
      toast.success("Demirbaş silindi");
      load();
    } catch (err) { toastApiError(err); }
  };

  const { panels, togglePanel, closePanel } = usePanels();
  const [warrantyDue, setWarrantyDue] = useState([]);
  const [insuranceDue, setInsuranceDue] = useState([]);
  useEffect(() => {
    http.get("/fixed-assets/warranty-due?days=30").then((r) => setWarrantyDue(r.data)).catch(() => {});
    http.get("/fixed-assets/insurance-due?days=30").then((r) => setInsuranceDue(r.data)).catch(() => {});
  }, [items]);
  const dispose = useDispose(load);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid && items.length) { const a = items.find((x) => x.id === eid); if (a) { startEdit(a); setSearchParams({}, { replace: true }); } }
  }, [items, searchParams, setSearchParams, startEdit]);
  const [qrSel, setQrSel] = useState([]);
  const [maintDue, setMaintDue] = useState([]);
  const loadDue = () => http.get("/fixed-assets/maintenance-due?days=30").then((r) => setMaintDue(r.data)).catch(() => {});
  useEffect(() => { loadDue(); }, []);

  return (
    <div className="space-y-6" data-testid="page-demirbaslar">
      <AssetToolbar editing={editing} disposedCount={disposedCount} showDisposed={showDisposed} setShowDisposed={setShowDisposed} panels={panels} togglePanel={togglePanel} startNew={startNew} />

      {panels.bulk && !editing && <AssetBulkAssign assets={items} personnel={personnel} onDone={load} onClose={() => closePanel("bulk")} />}
      {panels.report && !editing && <ZimmetReport onClose={() => closePanel("report")} />}
      {panels.count && !editing && <AssetCount assets={items} projects={projects.filter((p) => p.status === "Aktif")} onClose={() => closePanel("count")} />}
      {panels.depr && !editing && <DepreciationSchedule onClose={() => closePanel("depr")} isAdmin={isAdmin} />}
      {!editing && <WarrantyBanner items={warrantyDue} onOpen={openById} />}
      {!editing && <InsuranceBanner items={insuranceDue} onOpen={openById} />}
      {dispose.target && <DisposeForm asset={dispose.target} form={dispose.form} setForm={dispose.setForm} onCancel={() => dispose.setTarget(null)} onSave={dispose.save} busy={dispose.busy} />}
      {!editing && <MaintenanceDueBanner items={maintDue} onOpen={openById} />}
      {!editing && <QrBar qrSel={qrSel} setQrSel={setQrSel} />}

      {editing && <AssetForm editing={editing} form={form} setForm={setForm} submit={submit} cancel={cancel} depOptions={depOptions} pickDep={pickDep} projectOptions={projectOptions} assigneeOptions={assigneeOptions} pickProject={pickProject} pickAssignee={pickAssignee} isAdmin={isAdmin} load={load} loadDue={loadDue} />}

      <AssetBulkUpload onDone={load} />

      <AssetTable items={items} grouped={grouped} qrSel={qrSel} setQrSel={setQrSel} userLabels={userLabels} isAdmin={isAdmin} startEdit={startEdit} onDispose={dispose.open} undoDispose={dispose.undo} remove={remove} />
    </div>
  );
}
